import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../../common/CommonLoader";
import {
  SaveCoolerShipping,
  UpdateCoolerShipping,
  GetCoolerShippingById,
} from "../../../services/CoolerShippingService";

const AddCoolerShipping = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    coolerId: "",
    carrier: "",
    trackingNumber: "",
    labelId: "",
    labelUrl: "",
    pdfBase64: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchShippingData(parseInt(id, 10));
    }
  }, [id]);

  const fetchShippingData = async (shippingId) => {
    setLoading(true);
    try {
      const response = await GetCoolerShippingById(shippingId);
      if (response?.results) {
        setFormData(response.results);
      } else {
        NotificationManager.error("Failed to fetch shipping data");
      }
    } catch {
      NotificationManager.error("Error fetching shipping data");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.coolerId)
      newErrors.coolerId = "Cooler ID is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const shippingData = {
      ...formData,
      coolerId: formData.coolerId ? parseInt(formData.coolerId, 10) : null,
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id) {
        await UpdateCoolerShipping(shippingData);
        NotificationManager.success("Shipping record updated successfully");
      } else {
        await SaveCoolerShipping(shippingData);
        NotificationManager.success("Shipping record added successfully");
      }
      navigate("/cooler-shippings");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving shipping record");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCancel = () => navigate("/cooler-shippings");

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Cooler Shipping" : "Add New Cooler Shipping"}
        </Typography>

        <Grid container spacing={2}>
          {/* Cooler Identity */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Cooler ID"
              type="number"
              value={formData.coolerId || ""}
              onChange={handleChange("coolerId")}
              size="small"
              fullWidth
              required
              error={!!errors.coolerId}
              helperText={errors.coolerId}
            />
          </Grid>

          {/* Carrier Info */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Carrier"
              value={formData.carrier || ""}
              onChange={handleChange("carrier")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Tracking Number"
              value={formData.trackingNumber || ""}
              onChange={handleChange("trackingNumber")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Label Info */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Label ID"
              value={formData.labelId || ""}
              onChange={handleChange("labelId")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={8} md={6}>
            <TextField
              label="Label URL"
              value={formData.labelUrl || ""}
              onChange={handleChange("labelUrl")}
              size="small"
              fullWidth
              helperText="If provided, server will attempt to fetch PDF and store base64."
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={handleCancel} variant="outlined" color="secondary">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={handleSave} variant="contained" color="primary">
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddCoolerShipping;